import { useMemo } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { CartResponse } from "@/networking/carts.api";

export function useVgs(query: UseQueryResult<CartResponse | null>) {
  const isVgsEnabled = useMemo(() => {
    // First check if current payment method uses VGS
    const paymentMethod = query.data?.cart?.payment_method;
    if (
      paymentMethod &&
      "token_provider" in paymentMethod &&
      paymentMethod.token_provider === "vgs"
    ) {
      return true;
    }

    // If no payment method selected, check if any available payment method uses VGS
    const availablePaymentMethods =
      query.data?.cart?.available_payment_methods || [];
    const vgsPaymentMethod = availablePaymentMethods.find(
      (method) =>
        method.type === "credit_card" &&
        "token_provider" in method &&
        method.token_provider === "vgs",
    );

    return Boolean(vgsPaymentMethod);
  }, [
    query.data?.cart?.payment_method,
    query.data?.cart?.available_payment_methods,
  ]);

  const getPaymentAccountId = useMemo(() => {
    const cart = query.data?.cart;
    if (
      cart &&
      "payment_account" in cart &&
      typeof cart.payment_account === "string"
    ) {
      return cart.payment_account;
    }
    return undefined;
  }, [query.data?.cart]);

  const vgsPaymentMethods = useMemo(() => {
    return (
      query.data?.cart?.available_payment_methods?.filter(
        (method) =>
          method.type === "credit_card" &&
          "token_provider" in method &&
          method.token_provider === "vgs",
      ) || []
    );
  }, [query.data?.cart?.available_payment_methods]);

  const getPaymentAccountIdForType = useMemo(() => {
    return (paymentType: string) => {
      const availablePaymentMethods =
        query.data?.cart?.available_payment_methods || [];
      const paymentMethod = availablePaymentMethods.find(
        (method) => method.type === paymentType,
      );
      return paymentMethod && "id" in paymentMethod
        ? paymentMethod.id
        : undefined;
    };
  }, [query.data?.cart?.available_payment_methods]);

  return {
    isVgsEnabled,
    getPaymentAccountId,
    getPaymentAccountIdForType,
    vgsPaymentMethods,
    hasVgsSupport: vgsPaymentMethods.length > 0,
  };
}
